import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Spin, message } from 'antd';

import ModalWrapper from '../modal/ModalWrapper';
import { formatPhoneNumber } from '../../util/phoneNumber';
import { captureAnalytics } from '../../actions/preload';
import ReplyFaxNumberChoice from './ReplyFaxNumberChoice';
import {
  DOCUMENT_OPTION,
  LINE_OPTION,
  defaultReplyChoice,
  fetchReplyFaxDestination,
  replyChoiceRequest,
  saveReplyFaxNumber,
} from './replyFaxApi';
import { replySourceText } from './replyFaxText';

import './replyFax.scss';

const SAVE_ERRORS = {
  scope_not_allowed: 'You can\'t save a number for this line. Ask an admin.',
  blacklisted: 'This number is blocked for your organization.',
  invalid_number: 'This number no longer matches the fax. Close and reopen to see the latest.',
};

// Where a referral's replies are faxed, opened from the viewer header and the
// referral list. A person chooses the number (and whether the line keeps it),
// or an admin turns on replies for a line nobody set up.
const ReplyFaxNumberDialog = ({ hideModal, modalType }) => {
  const { sendToken } = modalType.data;
  const [destination, setDestination] = useState(null);
  const [choice, setChoice] = useState({ option: null, remember: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchReplyFaxDestination(sendToken).then((data) => {
      if (cancelled) return;
      setDestination(data);
      const automatic = data?.automatic_reply;
      if (automatic?.state === 'confirmed') {
        const option = automatic.destination === data.extracted_number ? DOCUMENT_OPTION : LINE_OPTION;
        setChoice({ option, remember: automatic.source === 'sender' });
      } else {
        setChoice(defaultReplyChoice(data));
      }
    }).catch(() => !cancelled && setError('Could not load the reply number. Try again.'));
    return () => { cancelled = true; };
  }, [sendToken]);

  const save = async (request) => {
    if (!request || saving) return;
    setSaving(true);
    setError(null);
    captureAnalytics('referral_reply_fax_number_saved', { scope: request.scope });
    try {
      const data = await saveReplyFaxNumber(sendToken, request);
      const number = formatPhoneNumber(data?.automatic_reply?.destination || request.faxNumber);
      message.success(data?.released_held_reply
        ? `Replies go to ${number}. The waiting reply is on its way.`
        : `Replies go to ${number}.`);
      hideModal();
    } catch (requestError) {
      setError(SAVE_ERRORS[requestError?.response?.data?.error] || 'Could not save the reply number. Try again.');
      setSaving(false);
    }
  };

  const automatic = destination?.automatic_reply;
  const notSetUp = automatic?.state === 'not_set_up';
  const request = replyChoiceRequest(destination, choice);

  let body;
  if (!destination && !error) {
    body = <div className="reply-fax-dialog__loading"><Spin /></div>;
  } else if (notSetUp) {
    body = (
      <>
        <p className="reply-fax-dialog__lead">
          {`The line this fax came from, ${formatPhoneNumber(destination.sender_fax_number)}, isn't set up for automatic replies, and the fax doesn't list a reply number.`}
        </p>
        {automatic.can_set_up_line ? (
          <Button
            loading={saving}
            onClick={() => save({ scope: 'line', faxNumber: destination.sender_fax_number })}
            data-testid="reply-fax-set-up-line"
          >
            {`Turn on replies to ${formatPhoneNumber(destination.sender_fax_number)}`}
          </Button>
        ) : (
          <p className="reply-fax-dialog__muted">
            An admin can turn on replies for this line. You can still send a reply yourself from Referral Reply.
          </p>
        )}
      </>
    );
  } else if (destination) {
    body = (
      <>
        <p className="reply-fax-dialog__lead">
          {automatic?.state === 'confirmed'
            ? `Replies go to ${formatPhoneNumber(automatic.destination)} (${replySourceText(automatic)}).`
            : 'Choose where this referral\'s replies are faxed.'}
          {automatic?.reply_waiting && ' An automatic reply is waiting and sends as soon as you save.'}
        </p>
        <ReplyFaxNumberChoice
          destination={destination}
          sendToken={sendToken}
          value={choice}
          onChange={setChoice}
          disabled={saving}
        />
      </>
    );
  }

  return (
    <ModalWrapper
      hideModal={hideModal}
      customContentStyle={{ width: '460px' }}
      modalTitle="Reply fax number"
      dismiss={saving ? '' : 'Cancel'}
      action={notSetUp ? undefined : () => save(request)}
      actionName={notSetUp || !destination ? null : 'Save'}
      disabled={!request || saving}
      isLoading={saving}
      isDraggable
    >
      <div className="reply-fax-dialog">
        {body}
        {error && <div className="reply-fax-dialog__error" role="alert">{error}</div>}
      </div>
    </ModalWrapper>
  );
};

ReplyFaxNumberDialog.propTypes = {
  hideModal: PropTypes.func.isRequired,
  modalType: PropTypes.shape({
    data: PropTypes.shape({ sendToken: PropTypes.string.isRequired }).isRequired,
  }).isRequired,
};

export default ReplyFaxNumberDialog;
